import type { AxiosInstance, AxiosRequestConfig } from "axios";
import { toFormUrlEncoded } from "../form.util";
import { makeLocalStorage } from "../storage";

// export type User = { id: string; email: string; name?: string };

export type LoginForm = {
    username: string;
    password: string;
    grant_type?: "password" | null;
};

export type LoginResponse = { access_token: string; token_type?: string };
export type VerifyResponse = {
    id: string;
    email: string;
    role: string;
    name?: string;
};
export type RefreshResponse = { access_token: string; token_type?: string };

function pickAccess(x: LoginResponse | RefreshResponse): string | null {
    return x?.access_token ?? null;
}

// добавление заголовков
function withHeaders(cfg: AxiosRequestConfig, headers: Record<string, string>): AxiosRequestConfig {
    return { ...cfg, headers: { ...(cfg.headers ?? {}), ...headers } };
}

type AuthServiceOptions = {
    storageKey: string;
    endpoints: { login: string; verify: string; refresh: string };
};

export type AuthService = {
    getAccessToken(): string | null;
    setAccessToken(token: string | null): void;
    isAuthenticated(): boolean;

    login(payload: LoginForm): Promise<string>;
    verify(token: string): Promise<VerifyResponse>;
    refresh(): Promise<string | null>;
    clear(): void;
};

export function createAuthService(api: AxiosInstance, opts: AuthServiceOptions): AuthService {
    const endpoints = opts.endpoints;

    const storage = makeLocalStorage(opts.storageKey);

    return {
        getAccessToken: () => storage.get(),
        setAccessToken: (t) => storage.set(t),
        isAuthenticated: () => !!storage.get(),

        async login(payload) {
            const body = toFormUrlEncoded({
                grant_type: payload.grant_type ?? "password",
                username: payload.username,
                password: payload.password,
            });

            const { data } = await api.post<LoginResponse>(
                endpoints.login,
                body,
                withHeaders(
                    {},
                    {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "X-Skip-Auth": "1",
                    },
                ),
            );

            const token = pickAccess(data);
            if (!token) throw new Error("No access token in /login response");
            storage.set(token);
            return token;
        },

        async verify(token: string) {
            const { data } = await api.post<VerifyResponse>(
                endpoints.verify,
                { token },
                withHeaders({}, { "X-Skip-Auth": "1" }),
            );
            return data;
        },

        async refresh() {
            try {
                const { data } = await api.post<RefreshResponse>(
                    endpoints.refresh,
                    undefined,
                    withHeaders({}, { "X-Skip-Auth": "1" }),
                );
                const token = pickAccess(data);
                storage.set(token ?? null);
                return storage.get();
            } catch {
                storage.clear();
                return null;
            }
        },

        clear() {
            storage.clear();
        },
    };
}
